import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('ready'); // 'ready', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validToken, setValidToken] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a valid password reset token
    const token_hash = searchParams.get('token_hash');
    const type = searchParams.get('type');

    if (!token_hash || type !== 'recovery') {
      setStatus('error');
      setMessage('Invalid password reset link. Please request a new password reset.');
      return;
    }

    setValidToken(true);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!newPassword || !confirmPassword) {
      setMessage('Please fill in all fields.');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error('Password reset error:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to reset password. Please try again.');
      } else {
        setStatus('success');
        setMessage('Password reset successfully! You can now sign in with your new password.');

        // Sign out the user after password reset
        await supabase.auth.signOut();
      }
    } catch (err) {
      console.error('Unexpected error during password reset:', err);
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again.');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!validToken && status === 'error') {
    return (
      <div className="page-container">
        <div className="page-content" style={{ textAlign: 'center', paddingTop: '60px' }}>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✗</div>
            <h1 style={{ color: '#d32f2f' }}>Invalid Link</h1>
            <p>{message}</p>
            <button
              onClick={handleGoHome}
              style={{
                marginTop: '30px',
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#4B9CD3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="page-container">
        <div className="page-content" style={{ textAlign: 'center', paddingTop: '60px' }}>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✓</div>
            <h1 style={{ color: '#4B9CD3' }}>Password Reset!</h1>
            <p>{message}</p>
            <p style={{ marginTop: '20px' }}>
              Return to the Puck Stats app and sign in with your new password.
            </p>
            <button
              onClick={handleGoHome}
              style={{
                marginTop: '30px',
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#4B9CD3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content" style={{ paddingTop: '60px' }}>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <h1 style={{ textAlign: 'center' }}>Reset Password</h1>
          <p style={{ textAlign: 'center', marginBottom: '30px' }}>
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="newPassword"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                }}
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                }}
                disabled={status === 'loading'}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="confirmPassword"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                }}
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                }}
                disabled={status === 'loading'}
              />
            </div>

            {message && (
              <div
                style={{
                  padding: '12px',
                  marginBottom: '20px',
                  backgroundColor: status === 'error' ? '#ffebee' : '#e3f2fd',
                  color: status === 'error' ? '#c62828' : '#1565c0',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                backgroundColor: status === 'loading' ? '#ccc' : '#4B9CD3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
              }}
            >
              {status === 'loading' ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={handleGoHome}
              style={{
                background: 'none',
                border: 'none',
                color: '#4B9CD3',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline',
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
